import React from 'react';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import ExpansionPanel from '@material-ui/core/ExpansionPanel';
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Box from '@material-ui/core/Box';
import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@material-ui/core';
import { t } from 'ttag';
import useReactRouter from 'use-react-router';
import { useApolloClient } from '@apollo/react-hooks';
import { GetApp as GetAppIcon } from '@material-ui/icons';

import { MyClubCApi } from '../../../api';
import Loading from '../../../components/Loading';
import { stripHtmlContent, toRichTextHtml } from '../../../components/RichTextEditor';
import { convertToYouTubeEmbed, YOUTUBE_EMBED_URL_PATTERN } from '../../../utils/youtube';

const hasRichText = (value) => stripHtmlContent(value || '').length > 0;

const InfoView = () => {
  const { match, history } = useReactRouter();
  const client = useApolloClient();
  const api = React.useMemo(() => new MyClubCApi(client), [client]);

  const dankaiIdParam = match.params.id || '';
  const dankaiId = Number(dankaiIdParam);
  const hasValidId = Number.isFinite(dankaiId) && !Number.isNaN(dankaiId);

  const [loading, setLoading] = React.useState(true);
  const [dankai, setDankai] = React.useState(null);
  const [errorDialog, setErrorDialog] = React.useState('');
  const [videoExpanded, setVideoExpanded] = React.useState({});
  const [generatingPdf, setGeneratingPdf] = React.useState(false);

  const openPdf = React.useCallback((url) => {
    if (!url) {
      return;
    }

    const ua = window.navigator.userAgent || '';
    const platform = window.navigator.platform || '';
    const maxTouchPoints = window.navigator.maxTouchPoints || 0;
    const isIos = /iP(ad|hone|od)/.test(ua) || (platform === 'MacIntel' && maxTouchPoints > 1);

    if (isIos) {
      window.location.href = url;
      return;
    }

    const newWindow = window.open(url, '_blank');
    if (newWindow) {
      newWindow.focus();
    } else {
      window.location.href = url;
    }
  }, []);

  const handleGeneratePdf = React.useCallback(async () => {
    if (!dankai || generatingPdf) {
      return;
    }

    if (!dankai.id) {
      setErrorDialog(t`Невірний данкай`);
      return;
    }

    setGeneratingPdf(true);
    try {
      const response = await api.generateDankaiPdf({ id: dankai.id });
      
      if (response.ok) {
        if (response.url) {
          // Відкриваємо PDF з урахуванням обмежень iOS Safari
          openPdf(response.url);
        } else {
          setErrorDialog(t`Не вдалося згенерувати PDF`);
        }
      } else {
        let errorMessage = t`Не вдалося згенерувати PDF`;
        if (response.errors && response.errors.length > 0) {
          const firstError = response.errors[0];
          if (firstError.messages && firstError.messages.length > 0) {
            errorMessage = firstError.messages[0];
          } else if (firstError.field) {
            errorMessage = `${t`Помилка в полі`} ${firstError.field}`;
          }
        }
        setErrorDialog(errorMessage);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to generate PDF', error);
      const errorMessage = error.message || error.toString() || t`Не вдалося згенерувати PDF`;
      setErrorDialog(errorMessage);
    } finally {
      setGeneratingPdf(false);
    }
  }, [api, dankai, generatingPdf, openPdf]);

  const renderItemLink = React.useCallback(
    (item, index, options = {}) => {
      if (!item.link) {
        return null;
      }

      const itemNameText = stripHtmlContent(item.name || '');
      const maybeEmbedLink = convertToYouTubeEmbed(item.link);
      if (YOUTUBE_EMBED_URL_PATTERN.test(maybeEmbedLink)) {
        const { videoExpanded: isVideoExpanded = false, onToggleVideo } = options;
        const videoTitle = itemNameText ? `${itemNameText} video` : `video-${index + 1}`;

        if (!onToggleVideo) {
          return (
            <Box mt={2} position="relative" paddingTop="56.25%">
              <iframe
                src={maybeEmbedLink}
                title={videoTitle}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
              />
            </Box>
          );
        }

        return (
          <Box mt={2} display="flex" flexDirection="column" alignItems="flex-start">
            <Button variant="outlined" size="small" onClick={onToggleVideo}>
              {isVideoExpanded ? t`Згорнути відео` : t`Розгорнути відео`}
            </Button>
            {isVideoExpanded ? (
              <Box mt={1} position="relative" paddingTop="56.25%" width="100%">
                <iframe
                  src={maybeEmbedLink}
                  title={videoTitle}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                />
              </Box>
            ) : null}
          </Box>
        );
      }

      return (
        <Box mt={1}>
          <a href={item.link} target="_blank" rel="noopener noreferrer">
            {t`Посилання`}
          </a>
        </Box>
      );
    },
    []
  );

  React.useEffect(() => {
    let isCancelled = false;

    const load = async () => {
      if (!hasValidId) {
        setLoading(false);
        setErrorDialog(t`Невірний ідентифікатор данкаю`);
        return;
      }

      setLoading(true);
      try {
        const detail = await api.getDankai({ dankaiId });
        if (isCancelled) {
          return;
        }
        if (!detail) {
          setErrorDialog(t`Данкай не знайдено`);
          return;
        }
        setDankai(detail);
      } catch (err) {
        if (!isCancelled) {
          // eslint-disable-next-line no-console
          console.error('Failed to load dankai info', err);
          setErrorDialog(t`Не вдалося завантажити данкай`);
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      isCancelled = true;
    };
  }, [api, dankaiId, hasValidId]);

  if (loading) {
    return <Loading />;
  }

  if (!dankai) {
    return (
      <Container maxWidth="lg">
        <Typography variant="h6">{t`Данкай не знайдено`}</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box
        display="flex"
        flexDirection={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
      >
        <Box display="flex" flexDirection="column" alignItems="flex-start">
          <Typography variant="h4" component="h1" style={{ marginRight: "50px", fontSize: "5rem", whiteSpace: "nowrap", flexShrink: 0 }}>
            {dankai.name}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<GetAppIcon />}
            onClick={handleGeneratePdf}
            disabled={generatingPdf}
            style={{ marginTop: 16 }}
          >
            {generatingPdf ? t`Генерація PDF...` : t`Згенерувати PDF`}
          </Button>
        </Box>
        <Box mt={{ xs: 2, sm: 0 }} textAlign="left" width={{ xs: '100%', sm: 'auto' }}>
          {dankai.theme && (
            <Typography>
              <Box component="span" fontWeight={600}>
                {t`Тема:`}
              </Box>{' '}
              {dankai.theme}
            </Typography>
          )}
          {dankai.criteria && (
            <Typography>
              <Box component="span" fontWeight={600}>
                {t`Критерії:`}
              </Box>{' '}
              {dankai.criteria}
            </Typography>
          )}
        </Box>
      </Box>
      <Box mt={3}>
        {(dankai.sections || []).map((section, si) => (
          <Paper key={section.id || si} elevation={1} style={{ marginBottom: 16 }}>
            <ExpansionPanel defaultExpanded>
              <ExpansionPanelSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`section-${si}-content`}
                id={`section-${si}-header`}
              >
                <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                  {`${si + 1}. ${section.name}`}
                </Typography>
              </ExpansionPanelSummary>
              <ExpansionPanelDetails>
                <Box width="100%">
                  {hasRichText(section.description) && (
                    <Typography
                      gutterBottom
                      component="div"
                      dangerouslySetInnerHTML={{ __html: toRichTextHtml(section.description) }}
                    />
                  )}
                  <Grid container spacing={0}>

                    {(section.items || []).map((item, ii) => {
                      const itemKey = item.id || `${si}-${ii}`;
                      const isYoutubeLink = Boolean(
                        item.link && YOUTUBE_EMBED_URL_PATTERN.test(convertToYouTubeEmbed(item.link))
                      );
                      const linkNode = isYoutubeLink
                        ? renderItemLink(item, ii, {
                            videoExpanded: Boolean(videoExpanded[itemKey]),
                            onToggleVideo: () =>
                              setVideoExpanded((prev) => ({
                                ...prev,
                                [itemKey]: !prev[itemKey],
                              })),
                          })
                        : renderItemLink(item, ii);

                      // Перевіряємо чи є контент для відображення в панелі
                      const hasDescription = hasRichText(item.description);
                      const hasTip = Boolean(item.tip);
                      const hasLink = Boolean(item.link);
                      const hasExpandableContent = hasDescription || hasTip || hasLink;

                      return (
                        <Grid item xs={12} key={item.id || ii}>
                          {hasExpandableContent ? (
                            <ExpansionPanel elevation={0}>
                              <ExpansionPanelSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls={`section-${si}-item-${ii}-content`}
                                id={`section-${si}-item-${ii}-header`}
                              >
                                <Grid container spacing={1} alignItems="flex-start">
                                  <Grid item style={{ flexShrink: 0 }}>
                                    <Typography variant="subtitle2" style={{ fontWeight: "normal" }}>
                                      {ii + 1}.
                                    </Typography>
                                  </Grid>
                                  <Grid item style={{ flex: 1 }}>
                                    <Typography
                                      variant="subtitle2"
                                      style={{ fontWeight: "normal" }}
                                      component="div"
                                      dangerouslySetInnerHTML={{ __html: toRichTextHtml(item.name) }}
                                    />
                                  </Grid>
                                </Grid>
                              </ExpansionPanelSummary>
                              <ExpansionPanelDetails>
                                <Box width="100%">
                                  {hasDescription && (
                                    <Typography
                                      paragraph
                                      component="div"
                                      style={{ fontStyle: 'italic' }}
                                      dangerouslySetInnerHTML={{ __html: toRichTextHtml(item.description) }}
                                    />
                                  )}
                                  {hasTip && (
                                    <Typography variant="body2" paragraph>
                                      {t`Порада:`} {item.tip}
                                    </Typography>
                                  )}
                                  {linkNode}
                                </Box>
                              </ExpansionPanelDetails>
                            </ExpansionPanel>
                          ) : (
                            // Якщо немає контенту для розгортання, показуємо тільки назву завдання
                            <Box p={2} >
                              <Grid container spacing={1} alignItems="flex-start">
                                <Grid item style={{ flexShrink: 0 }}>
                                  <Typography variant="subtitle2" style={{ fontWeight: "normal" }}>
                                    {ii + 1}.
                                  </Typography>
                                </Grid>
                                <Grid item style={{ flex: 1 }}>
                                  <Typography
                                    variant="subtitle2"
                                    style={{ fontWeight: "normal" }}
                                    component="div"
                                    dangerouslySetInnerHTML={{ __html: toRichTextHtml(item.name) }}
                                  />
                                </Grid>
                              </Grid>
                            </Box>
                          )}
                        </Grid>
                      );
                    })}

                  </Grid>
                </Box>
              </ExpansionPanelDetails>
            </ExpansionPanel>
          </Paper>
        ))}
      </Box>

      <Dialog open={Boolean(errorDialog)} onClose={() => {
        setErrorDialog('');
        history.goBack();
      }}>
        <DialogTitle>{t`Помилка`}</DialogTitle>
        <DialogContent>
          <DialogContentText>{errorDialog}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            color="primary"
            onClick={() => {
              setErrorDialog('');
              history.goBack();
            }}
          >
            {t`Повернутись`}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default InfoView;
